import { Button } from "@dash/form";
import { SectionHeader } from "@dash/page";
import { Pagination } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { useTheme } from "@emotion/react";
import { formatCurrency } from "@legacy-megarax/react-utils";
import { buildCartsListUrlForCustomer } from "@megarax/loyalty-carts-admin";
import { formatDistanceToNowStrict } from "date-fns";
import { pl } from "date-fns/locale";
import React, { useState } from "react";
import { MdChevronRight, MdShoppingCart } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom-v5-compat";

import { Order } from "@megaron/fulfillment-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";
import { getMegaraxPath } from "@megaron/megarax-uri";

type Props = {
  customerUuid: Uuid;
};

const OrderItem: React.FC<{ order: Order }> = ({ order }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.mediumBorderRadius,
        overflow: "hidden",
        backgroundColor: theme.colors.background,
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          justifyContent: "space-between",
          padding: "1rem",
          backgroundColor: isOpen ? theme.colors.background : "transparent",
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          border: "none",
          textAlign: "left",
          "&:hover": {
            backgroundColor: theme.colors.background,
          },
        }}
      >
        <div css={{ display: "flex", flexDirection: "column", flex: 1, gap: "0.25rem" }}>
          <Link
            to={getMegaraxPath.uri("megarax://fulfillment/orders/id/{id}", { id: order.id })}
            onClick={(e) => e.stopPropagation()}
            css={{
              cursor: "pointer",
              color: theme.colors.primary,
              fontWeight: 600,
              fontSize: "0.9375rem",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            WEB-{order.orderNumber}
          </Link>
          <span css={{ color: theme.colors.secondaryText, fontSize: "0.8125rem" }}>
            {formatDistanceToNowStrict(order.processedAt, { locale: pl, addSuffix: true })}
          </span>
        </div>
        <div css={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span css={{ fontWeight: 600, fontSize: "0.9375rem", color: theme.colors.default }}>
            {formatCurrency(order.totalPrice, order.currency)}
          </span>
          <MdChevronRight
            size={24}
            css={{
              color: theme.colors.secondaryText,
              flexShrink: 0,
              transform: isOpen ? "rotate(-90deg)" : "rotate(90deg)",
              transition: "transform 0.2s ease-in-out",
            }}
          />
        </div>
      </button>
      {isOpen && (
        <div
          css={{
            padding: "1rem",
            borderTop: `1px solid ${theme.colors.border}`,
            backgroundColor: theme.colors.background,
            animation: "slideDown 0.2s ease-out",
            "@keyframes slideDown": {
              from: {
                opacity: 0,
                maxHeight: 0,
              },
              to: {
                opacity: 1,
                maxHeight: "1000px",
              },
            },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {order.items && order.items.length > 0 ? (
              <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {order.items.map((item, idx) => (
                  <div
                    key={idx}
                    css={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      padding: "0.75rem",
                      backgroundColor: theme.colors.selectedBackground,
                      borderRadius: theme.smallBorderRadius,
                      fontSize: "0.875rem",
                      gap: "1rem",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: theme.colors.hover || theme.colors.selectedBackground,
                      },
                    }}
                  >
                    <span css={{ flex: 1, lineHeight: 1.5 }}>{item.name}</span>
                    <div css={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
                      <span css={{ color: theme.colors.secondaryText, fontSize: "0.8125rem" }}>x{item.quantity}</span>
                      <span css={{ fontWeight: 600, color: theme.colors.default }}>
                        {formatCurrency(item.price, order.currency)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <span css={{ color: theme.colors.secondaryText, fontSize: "0.875rem", fontStyle: "italic" }}>
                Brak produktów
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const LoyaltyFulfillmentOrders: React.FC<Props> = ({ customerUuid }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const pageSize = 5;

  const ordersQuery = useClientManager("fulfillment")
    .ordersQuery()
    .useQuery({
      userId: customerUuid,
      limit: pageSize,
      offset: page * pageSize,
    });

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <SectionHeader
        isHr
        actions={
          <Button
            icon={<MdShoppingCart />}
            variant="outline"
            size="small"
            onClick={() => navigate(buildCartsListUrlForCustomer(customerUuid))}
          >
            Koszyki klienta
          </Button>
        }
      >
        Historia zamówień
      </SectionHeader>

      {(ordersQuery.data?.count ?? 0) > pageSize && (
        <div
          css={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Pagination
            page={page}
            onPageChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            pageSize={pageSize}
            itemCount={ordersQuery.data?.count ?? 0}
          />
        </div>
      )}

      <QuerySkeleton query={ordersQuery}>
        {(orders) =>
          orders.items.length > 0 ? (
            <>
              <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {orders.items.map((order) => (
                  <OrderItem key={order.id} order={order} />
                ))}
              </div>
            </>
          ) : (
            <span css={{ color: theme.colors.secondaryText, fontSize: "1rem", marginTop: "-0.5rem" }}>
              Brak zamówień
            </span>
          )
        }
      </QuerySkeleton>
    </div>
  );
};
